// Árvore de rotas do back-office admin, extraída do App.tsx para ser renderizada POR ABA dentro do
// TabKeepAliveHost. Caminhos ABSOLUTOS sob um layout com pathnameBase "/" (sem splat) — não dependem de
// resolução relativa de splat.
//
// Gates cientes de aba (tratamento do risco de keep-alive): numa aba INATIVA, um gate sem acesso NÃO pode
// redirecionar (sequestraria a URL da aba ativa) nem expor conteúdo — por isso renderiza um fallback
// "sem acesso". Na aba ATIVA o comportamento é o de produção: redireciona para a home autorizada.
import { useContext, useEffect } from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { Loader2, ShieldAlert } from "lucide-react";
import { useApp } from "@/store/useApp";
import { hasAnyPermission, hasPermission, homeRouteFor } from "@/lib/types";
import { staffEntryRoute } from "@/lib/staffWelcome";
import { TabRuntimeContext, useTabActive } from "@/layouts/adminTabs/tabRuntime";
import { useAdminTabs } from "@/store/useAdminTabs";
import NotFound from "@/pages/NotFound";
import AdminClients from "@/pages/admin/AdminClients";
import AdminNewOrder from "@/pages/admin/AdminNewOrder";
import AdminPriceTables from "@/pages/admin/AdminPriceTables";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminProductCosts from "@/pages/admin/AdminProductCosts";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminOperations from "@/pages/admin/AdminOperations";
import AdminFuncionarios from "@/pages/admin/AdminFuncionarios";
import AdminDeliveryConfig from "@/pages/admin/AdminDeliveryConfig";
import AdminSuppliers from "@/pages/admin/AdminSuppliers";
import AdminSalesReport from "@/pages/admin/AdminSalesReport";
import AdminProfitability from "@/pages/admin/AdminProfitability";
import AdminOrderDetail from "@/pages/admin/AdminOrderDetail";
import AdminPurchaseListReport from "@/pages/admin/AdminPurchaseListReport";
import AdminStock from "@/pages/admin/AdminStock";
import AdminPackagingMaterials from "@/pages/admin/AdminPackagingMaterials";
import AdminReceivings from "@/pages/admin/AdminReceivings";
import AdminSupplierInvoices from "@/pages/admin/AdminSupplierInvoices";
import AdminCertificates from "@/pages/admin/AdminCertificates";
import AdminEstablishments from "@/pages/admin/AdminEstablishments";
import AdminEstablishmentDetail from "@/pages/admin/AdminEstablishmentDetail";
import AdminDfeCollector from "@/pages/admin/AdminDfeCollector";
import AdminDeliveryUnits from "@/pages/admin/AdminDeliveryUnits";
import AdminProductionReport from "@/pages/admin/AdminProductionReport";
import AdminOrdersReport from "@/pages/admin/AdminOrdersReport";
import AdminProductReport from "@/pages/admin/AdminProductReport";
import AdminPricingReport from "@/pages/admin/AdminPricingReport";
import AdminReceivingsReport from "@/pages/admin/AdminReceivingsReport";
import AdminReceivables from "@/pages/admin/AdminReceivables";
import AdminFinanceResults from "@/pages/admin/AdminFinanceResults";
import DashboardCompras from "@/pages/admin/DashboardCompras";
import DashboardProducao from "@/pages/admin/DashboardProducao";
import DashboardExpedicao from "@/pages/admin/DashboardExpedicao";
import AdminDeliveries from "@/pages/admin/AdminDeliveries";
import AdminPermissions from "@/pages/admin/AdminPermissions";
import AdminVehicles from "@/pages/admin/AdminVehicles";
import AdminVehicleDetail from "@/pages/admin/AdminVehicleDetail";
import AdminSelection from "@/pages/admin/AdminSelection";
import AdminSelectionCandidates from "@/pages/admin/AdminSelectionCandidates";
import AdminSelectionEvaluation from "@/pages/admin/AdminSelectionEvaluation";
import AdminSelectionRanking from "@/pages/admin/AdminSelectionRanking";
import AdminSelectionSettings from "@/pages/admin/AdminSelectionSettings";
import AdminBoxControl from "@/pages/admin/AdminBoxControl";
import AdminBoxCatalog from "@/pages/admin/AdminBoxCatalog";
import AdminBoxPrices from "@/pages/admin/AdminBoxPrices";
import HelpCenter from "@/pages/help/HelpCenter";
import AdminHelpArticles from "@/pages/admin/AdminHelpArticles";
import AdminWelcome from "@/pages/admin/AdminWelcome";

/** Aba inativa sem acesso: não redireciona e não mostra o conteúdo protegido. */
function GateBlocked() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center p-6">
      <div role="alert" className="flex max-w-md items-start gap-3 rounded-lg border border-border bg-muted/30 p-4">
        <ShieldAlert className="mt-0.5 h-5 w-5 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          Você não tem mais acesso a esta tela. Selecione outra aba.
        </p>
      </div>
    </div>
  );
}

/**
 * Em aba ATIVA, uma rota sem acesso não pode deixar uma aba interna vazia aberta (ex.: acesso direto por
 * URL a uma rota adminOnly): fecha a própria aba e vai para a aba vizinha ou para a home autorizada. Em aba
 * INATIVA, mostra o fallback sem navegar (não sequestra a URL da aba ativa).
 */
function Blocked({ to }: { to: string }) {
  const active = useTabActive();
  const { tabKey } = useContext(TabRuntimeContext);
  const performClose = useAdminTabs((s) => s.performClose);
  const navigate = useNavigate();

  useEffect(() => {
    if (!active) return;
    const { nextLocation } = performClose(tabKey);
    navigate(nextLocation ? { pathname: nextLocation.pathname, search: nextLocation.search } : to, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, tabKey]);

  if (!active) return <GateBlocked />;
  return null;
}

function RequirePermission({ permission, children }: { permission: string; children: React.ReactNode }) {
  const { account } = useApp();
  if (!hasPermission(account, permission)) return <Blocked to={homeRouteFor(account)} />;
  return <>{children}</>;
}

function RequireAnyPermission({ anyOf, children }: { anyOf: string[]; children: React.ReactNode }) {
  const { account } = useApp();
  if (!hasAnyPermission(account, ...anyOf)) return <Blocked to={homeRouteFor(account)} />;
  return <>{children}</>;
}

function RequireStrictAdmin({ children }: { children: React.ReactNode }) {
  const { account } = useApp();
  if (account?.role !== "ADMIN") return <Blocked to={homeRouteFor(account)} />;
  return <>{children}</>;
}

function RequireSupplierNfEnabled({ children }: { children: React.ReactNode }) {
  const account = useApp((state) => state.account);
  const supplierNfEnabled = useApp((state) => state.supplierNfEnabled);
  const moduleFlagsStatus = useApp((state) => state.moduleFlagsStatus);
  const loadModuleFlags = useApp((state) => state.loadModuleFlags);

  if (moduleFlagsStatus === "loading") {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-muted-foreground" role="status">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Carregando…
      </div>
    );
  }
  if (moduleFlagsStatus === "error") {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 p-6 text-center" role="alert">
        <p className="text-sm text-muted-foreground">
          Não foi possível verificar a disponibilidade do módulo de NF de fornecedor.
        </p>
        <button
          type="button"
          onClick={() => void loadModuleFlags()}
          className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted"
        >
          Tentar novamente
        </button>
      </div>
    );
  }
  if (!supplierNfEnabled) return <Blocked to={homeRouteFor(account, false)} />;
  return <>{children}</>;
}

export function AdminRoutes() {
  const account = useApp((state) => state.account);
  const supplierNfEnabled = useApp((state) => state.supplierNfEnabled);
  // "/admin" puro é entrada no ambiente: respeita a preferência de página inicial (boas-vindas ou Dashboard).
  const initialRoute = staffEntryRoute(account, supplierNfEnabled);

  return (
    <Routes>
      <Route path="/admin" element={<Navigate to={initialRoute} replace />} />
      <Route path="/admin/boas-vindas" element={<AdminWelcome />} />
      <Route path="/dashboard/operacoes" element={<RequirePermission permission="PEDIDOS.PEDIDO.VISUALIZAR"><AdminDashboard /></RequirePermission>} />
      <Route path="/dashboard/compras" element={<RequirePermission permission="PEDIDOS.COMPRA.AVANCAR"><DashboardCompras /></RequirePermission>} />
      <Route path="/dashboard/producao" element={<RequireAnyPermission anyOf={["PEDIDOS.PRODUCAO.AVANCAR", "PEDIDOS.PRODUCAO.IN_NATURA"]}><DashboardProducao /></RequireAnyPermission>} />
      <Route path="/dashboard/expedicao" element={<RequirePermission permission="PEDIDOS.EXPEDICAO.AVANCAR"><DashboardExpedicao /></RequirePermission>} />
      <Route path="/admin/operacoes" element={<RequirePermission permission="PEDIDOS.PEDIDO.VISUALIZAR"><AdminOperations /></RequirePermission>} />
      <Route path="/admin/pedidos/novo" element={<RequirePermission permission="PEDIDOS.PEDIDO.CRIAR_MANUAL"><AdminNewOrder /></RequirePermission>} />
      <Route path="/admin/clientes" element={<RequirePermission permission="CLIENTES.CLIENTE.VISUALIZAR"><AdminClients /></RequirePermission>} />
      <Route path="/admin/tabelas" element={<RequirePermission permission="PRECOS.TABELA.VISUALIZAR"><AdminPriceTables /></RequirePermission>} />
      <Route path="/admin/produtos" element={<RequirePermission permission="CATALOGO.PRODUTO.VISUALIZAR"><AdminProducts /></RequirePermission>} />
      <Route path="/admin/custos" element={<RequirePermission permission="CATALOGO.CUSTO.VISUALIZAR"><AdminProductCosts /></RequirePermission>} />
      <Route path="/admin/funcionarios" element={<RequirePermission permission="STAFF.FUNCIONARIO.VISUALIZAR"><AdminFuncionarios /></RequirePermission>} />
      <Route path="/admin/permissoes" element={<RequirePermission permission="STAFF.FUNCIONARIO.VISUALIZAR"><AdminPermissions /></RequirePermission>} />
      <Route path="/admin/configuracoes/entregas" element={<RequirePermission permission="ENTREGAS.CONFIG.VISUALIZAR"><AdminDeliveryConfig /></RequirePermission>} />
      <Route path="/admin/fornecedores" element={<RequirePermission permission="SUPRIMENTOS.FORNECEDOR.VISUALIZAR"><AdminSuppliers /></RequirePermission>} />
      <Route path="/admin/caixas" element={<RequirePermission permission="CAIXAS.MOVIMENTACAO.VISUALIZAR"><AdminBoxControl /></RequirePermission>} />
      <Route path="/admin/caixas/materiais" element={<RequireStrictAdmin><AdminBoxCatalog /></RequireStrictAdmin>} />
      <Route path="/admin/caixas/precos" element={<RequireStrictAdmin><AdminBoxPrices /></RequireStrictAdmin>} />
      <Route path="/admin/estoque" element={<RequirePermission permission="SUPRIMENTOS.ESTOQUE.VISUALIZAR"><AdminStock /></RequirePermission>} />
      <Route path="/admin/embalagens" element={<RequirePermission permission="SUPRIMENTOS.EMBALAGEM.VISUALIZAR"><AdminPackagingMaterials /></RequirePermission>} />
      <Route path="/admin/recebimentos" element={<RequirePermission permission="SUPRIMENTOS.RECEBIMENTO.VISUALIZAR"><AdminReceivings /></RequirePermission>} />
      <Route path="/admin/nf-entrada" element={<RequireSupplierNfEnabled><RequirePermission permission="FINANCEIRO.NF_ENTRADA.VISUALIZAR"><AdminSupplierInvoices /></RequirePermission></RequireSupplierNfEnabled>} />
      <Route path="/admin/certificados" element={<RequireSupplierNfEnabled><RequirePermission permission="FINANCEIRO.CERTIFICADO.VISUALIZAR"><AdminCertificates /></RequirePermission></RequireSupplierNfEnabled>} />
      <Route path="/admin/estabelecimentos" element={<RequireSupplierNfEnabled><RequirePermission permission="FINANCEIRO.ESTABELECIMENTO.VISUALIZAR"><AdminEstablishments /></RequirePermission></RequireSupplierNfEnabled>} />
      <Route path="/admin/estabelecimentos/:id" element={<RequireSupplierNfEnabled><RequirePermission permission="FINANCEIRO.ESTABELECIMENTO.VISUALIZAR"><AdminEstablishmentDetail /></RequirePermission></RequireSupplierNfEnabled>} />
      <Route path="/admin/buscador-nfe" element={<RequireSupplierNfEnabled><RequireStrictAdmin><AdminDfeCollector /></RequireStrictAdmin></RequireSupplierNfEnabled>} />
      <Route path="/admin/relatorios/vendas" element={<RequirePermission permission="RELATORIOS.VENDAS.VISUALIZAR"><AdminSalesReport /></RequirePermission>} />
      <Route path="/admin/pedidos/:orderId" element={<RequirePermission permission="PEDIDOS.PEDIDO.VISUALIZAR"><AdminOrderDetail /></RequirePermission>} />
      <Route path="/admin/relatorios/lista-de-compras" element={<RequirePermission permission="RELATORIOS.LISTA_COMPRAS.VISUALIZAR"><AdminPurchaseListReport /></RequirePermission>} />
      <Route path="/admin/relatorios/precificacao" element={<RequirePermission permission="RELATORIOS.PRECIFICACAO.VISUALIZAR"><AdminPricingReport /></RequirePermission>} />
      <Route path="/admin/relatorios/rentabilidade" element={<RequirePermission permission="RELATORIOS.RENTABILIDADE.VISUALIZAR"><AdminProfitability /></RequirePermission>} />
      <Route path="/admin/unidades-entrega" element={<RequirePermission permission="CLIENTES.UNIDADE.VISUALIZAR"><AdminDeliveryUnits /></RequirePermission>} />
      <Route path="/admin/relatorios/producao" element={<RequirePermission permission="RELATORIOS.PRODUCAO.VISUALIZAR"><AdminProductionReport /></RequirePermission>} />
      <Route path="/admin/relatorios/pedidos" element={<RequirePermission permission="RELATORIOS.PEDIDOS.VISUALIZAR"><AdminOrdersReport /></RequirePermission>} />
      <Route path="/admin/relatorios/produtos" element={<RequirePermission permission="RELATORIOS.PRODUTOS.VISUALIZAR"><AdminProductReport /></RequirePermission>} />
      <Route path="/admin/relatorios/recebimentos" element={<RequirePermission permission="RELATORIOS.RECEBIMENTOS.VISUALIZAR"><AdminReceivingsReport /></RequirePermission>} />
      <Route path="/admin/entregas" element={<RequirePermission permission="ENTREGAS.ROTA.VISUALIZAR"><AdminDeliveries /></RequirePermission>} />
      <Route path="/admin/financeiro/contas-a-receber" element={<RequirePermission permission="FINANCEIRO.CONTAS_RECEBER.VISUALIZAR"><AdminReceivables /></RequirePermission>} />
      <Route path="/admin/financeiro/resultados" element={<RequirePermission permission="FINANCEIRO.RESULTADOS.VISUALIZAR"><AdminFinanceResults /></RequirePermission>} />
      <Route path="/admin/frota" element={<RequirePermission permission="FROTA.VEICULO.VISUALIZAR"><AdminVehicles /></RequirePermission>} />
      <Route path="/admin/frota/:id" element={<RequirePermission permission="FROTA.VEICULO.VISUALIZAR"><AdminVehicleDetail /></RequirePermission>} />
      <Route path="/admin/selecao" element={<RequirePermission permission="SELECAO.PROCESSO.VISUALIZAR"><AdminSelection /></RequirePermission>} />
      <Route path="/admin/selecao/:processId/candidatos" element={<RequirePermission permission="SELECAO.CANDIDATO.VISUALIZAR"><AdminSelectionCandidates /></RequirePermission>} />
      <Route path="/admin/selecao/:processId/ranking" element={<RequirePermission permission="SELECAO.RANKING.VISUALIZAR"><AdminSelectionRanking /></RequirePermission>} />
      <Route path="/admin/selecao/:processId/ajustes" element={<RequirePermission permission="SELECAO.AJUSTE.VISUALIZAR"><AdminSelectionSettings /></RequirePermission>} />
      <Route path="/admin/selecao/candidatos/:enrollmentId" element={<RequireAnyPermission anyOf={["SELECAO.CANDIDATO.AVALIAR", "SELECAO.CANDIDATO.VISUALIZAR"]}><AdminSelectionEvaluation /></RequireAnyPermission>} />
      <Route path="/admin/ajuda" element={<RequirePermission permission="AJUDA.CENTRAL.VISUALIZAR"><HelpCenter /></RequirePermission>} />
      <Route path="/admin/ajuda/gerenciar" element={<RequireStrictAdmin><AdminHelpArticles /></RequireStrictAdmin>} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
